<template>
  <vx-card
    no-shadow
    class="px-base app-checkout">
    <template v-slot:no-body>
      <vue-element-loading
        :active="loading"
        color="rgba(var(--vs-primary), 1)"
        spinner="mini-spinner"/>
      <div
        v-if="recurrent && showInputIntervals"
        class="mb-6">
        <recurrent-input-interval
          v-model="selectedInterval"
          :inactive-intervals="inactiveIntervals"
        />

        <div
          v-if="$slots['after-recurrent']"
          class="mt-3">
          <slot name="after-recurrent"></slot>
        </div>
      </div>

      <div class="flex justify-between mb-base">
        <div>
          <p class="font-semibold">{{ productName }}</p>
          <small
            v-if="recurrent"
            class="text-grey">{{ billedRecurrentLabel }}</small>
        </div>
        <span class="font-semibold">{{ productPriceWithDiscount | dollar }}</span>
      </div>

      <div
        v-for="(item, index) in extraItems"
        :key="`${item.name}-${index}`"
        class="flex justify-between  mb-base">
        <div>
          <p class="font-semibold">{{ item.name }}</p>
          <small
            v-if="item.recurrent"
            class="text-grey">{{ $t('BilledMonthly') }}</small>
          <small
            v-else-if="item.onlyFirstPay"
            class="text-grey">{{ $t('OneOff') }}</small>
        </div>
        <span class="font-semibold">{{ item.price | dollar }}</span>
      </div>

      <div class="flex justify-between font-semibold">
        <span>{{ $t('$General.Subtotal') }}</span>
        <span>{{ subtotal | dollar }}</span>
      </div>

      <vs-divider></vs-divider>

      <div class="mb-base">
        <div
          v-if="!addPromotionCode"
          class="w-full flex justify-end"
        >
          <a
            href="#"
            class="link-cta"
            @click.prevent="addPromotionCode = !addPromotionCode">
            {{ $t('AddPromotionCode') }}
          </a>
        </div>

        <div
          v-if="addPromotionCode && (!appliedPromotionCode || appliedPromoCodeIsInvalid)"
          class="promo-code-container">
          <vs-input
            v-model="promoCode"
            :placeholder="$t('AddPromotionCode')"
            class="w-full"
            :danger="appliedPromoCodeIsInvalid"
            :danger-text="$t('InvalidPromoCode')"
            val-icon-danger="clear"
            @input="appliedPromotionCode = undefined"
          ></vs-input>
          <a
            v-if="promoCode && !appliedPromoCodeIsInvalid"
            href="#"
            class="link-plain apply-button"
            @click.prevent="applyPromoCode">
            {{ $t('$General.Apply') }}
          </a>
        </div>

        <div v-if="appliedPromotionCode && !appliedPromoCodeIsInvalid">
          <div class="flex justify-between text-grey mb-2">
            <div class="flex flex-col">
              <vs-chip
                closable
                @click="removePromoCode">
                <vs-avatar
                  icon="local_offer" />
                {{ appliedPromotionCode.code }}
              </vs-chip>
              <small class="pl-3">
                {{ promoCodeOffText }}
              </small>
            </div>
            <span>{{ subtotal | dollar }}</span>
          </div>
        </div>
      </div>

      <div
        v-for="(tax, index) in taxBreakdownFormatted"
        :key="index"
        class="flex justify-between text-grey mb-2">
        <span>{{ tax.label }}</span>
        <span>{{ tax.amount | dollar }}</span>
      </div>

      <vs-divider />

      <div class="flex justify-between font-bold text-lg mb-base">
        <span>{{ $t('$General.Total') }}</span>
        <span>{{ total | dollar }}</span>
      </div>

      <div
        v-if="paymentMethods.length > 0"
        class="pt-3">
        <div class="flex justify-between mb-3">
          <label>{{ $t('PayWith') }}</label>
          <vs-checkbox
            v-model="payWithNewCard">
            {{ $t('PayWithNewCard') | lowercase }}
          </vs-checkbox>
        </div>
        <template v-if="!payWithNewCard">
          <v-select-payment-method
            v-model="selectedPaymentMethodLocal"
            :payment-methods="paymentMethods"
            append-to-body
            :danger="!!paymentFailedMsg"
            @input="inputSelectedPaymentMethod()"/>
          <span
            v-if="!!paymentFailedMsg"
            class="text-danger">{{ paymentFailedMsg }}</span>
        </template>
      </div>

      <div
        v-if="payWithNewCard"
        class="vx-card border no-shadow mt-5">
        <add-credit-card
          ref="addCreditCard"
          :addButton="false"
          :errorCard="!!paymentFailedMsg"
          @is-valid="(val) => isCreditCardValid=val"
          @change="$emit('input-payment-method')"/>
        <span
          v-if="!!paymentFailedMsg"
          class="text-danger">{{ paymentFailedMsg }}</span>
      </div>

      <div
        v-if="$slots['before-pay-button']"
        class="mt-5">
        <slot name="before-pay-button"></slot>
      </div>

      <vs-button
        :disabled="!isValidPay"
        class="w-full font-bold mt-base mb-5"
        @click="paymentIntent()">
        {{ $t('$General.Pay') }} {{ total | dollar }}
      </vs-button>
    </template>

  </vx-card>
</template>

<script>
// Components
import AddCreditCard from '@/views/modules/_components/AddCreditCard.vue';
import AppCheckoutRecurrentInputInterval from '@/views/modules/_components/app-checkout/AppCheckoutRecurrentInputInterval.vue';
import VSelectPaymentMethod from '@/views/modules/_components/VSelectPaymentMethod.vue';

// Mixins
import commonVSelect from '@/views/modules/_mixins/commonVSelect';

// Util
import enums from '@/enums';
import tenantService from '@/api/modules/tenant.service';

/**
 * App checkout
 *
 * @module views/modules/components/AppCheckout
 * @author Dilan Useche <dilan8810@gmail.com>
 *
 */
export default {
  name: 'AppCheckout',
  i18n: {
    messages: {
      en: {
        BilledMonthly: 'Billed monthly',
        BilledQuarterly: 'Billed quarterly',
        BilledSemiAnnually: 'Billed semi-annually',
        BilledYearly: 'Billed yearly',
        OneOff: 'One-off',
        GstAmountLabel: 'GST ({percent}%)',
        PayWith: 'Pay with',
        PayWithNewCard: 'New card',
        AddPromotionCode: 'Add promotion code',
        PromoCodeOff: '{amount} off',
        InvalidPromoCode: 'This promotion code is invalid.',
      },
    },
  },
  components: {
    AddCreditCard,
    RecurrentInputInterval: AppCheckoutRecurrentInputInterval,
    VSelectPaymentMethod,
  },
  mixins: [commonVSelect],
  props: {
    productName: {
      type: String,
      required: true,
    },
    productPrice: {
      type: Number,
      required: true,
    },
    productStripeId: {
      type: String,
      required: true,
    },
    gst: {
      type: Number,
      required: true,
    },
    recurrent: {
      type: Boolean,
      default: false,
    },
    interval: {
      type: String,
      validator(value) {
        return Object.values(enums.Tenants.APP_SERVICE_ITEM_BILLING_INTERVAL).indexOf(value) !== -1;
      },
      default: enums.Tenants.APP_SERVICE_ITEM_BILLING_INTERVAL.MONTH,
    },
    inactiveIntervals: {
      type: Array,
      required: false,
      validator(intervals) {
        return intervals.every(
          (interval) => Object.values(
            enums.Tenants.APP_SERVICE_ITEM_BILLING_INTERVAL,
          ).indexOf(interval) !== -1,
        );
      },
      default() {
        return [];
      },
    },
    showInputIntervals: {
      type: Boolean,
      required: false,
      default: true,
    },
    paymentMethods: {
      type: Array,
      required: true,
    },
    selectedPaymentMethod: {
      required: true,
    },
    extraItems: {
      type: Array,
      required: false,
      default() {
        return [];
      },
    },
    paymentFailedMsg: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      selectedInterval: this.interval,
      selectedPaymentMethodLocal: this.selectedPaymentMethod,
      payWithNewCard: this.paymentMethods.length === 0,
      isCreditCardValid: false,
      taxCalculation: null,
      loading: false,
      addPromotionCode: false,
      promoCode: '',
      appliedPromotionCode: undefined,
    };
  },
  computed: {
    appliedPromoCodeIsInvalid() {
      if (this.appliedPromotionCode === undefined) {
        return false;
      }

      return this.appliedPromotionCode === null || !this.appliedPromotionCodeContainsStripeProduct
      || (this.appliedPromotionCode && !this.appliedPromotionCode.active);
    },
    appliedPromotionCodeContainsStripeProduct() {
      if (this.appliedPromotionCode === undefined) {
        return true;
      }

      return this.appliedPromotionCode && (!this.appliedPromotionCode.coupon.applies_to
      || (this.appliedPromotionCode.coupon.applies_to.products.includes(this.productStripeId)));
    },
    promoCodeOffText() {
      if (!this.appliedPromotionCode) {
        return '';
      }

      if (this.appliedPromotionCode.coupon.amount_off) {
        return this.$t('PromoCodeOff', {
          amount: this.$options.filters.dollar(this.appliedPromotionCode.coupon.amount_off / 100),
        });
      }

      if (this.appliedPromotionCode.coupon.percent_off) {
        return this.$t('PromoCodeOff', {
          amount: this.$options.filters.percent(this.appliedPromotionCode.coupon.percent_off),
        });
      }

      return '';
    },
    billedRecurrentLabel() {
      switch (this.selectedInterval) {
        case this.$enums.Tenants.APP_SERVICE_ITEM_BILLING_INTERVAL.TRIMESTER:
          return this.$t('BilledQuarterly');
        case this.$enums.Tenants.APP_SERVICE_ITEM_BILLING_INTERVAL.SEMESTER:
          return this.$t('BilledSemiAnnually');
        case this.$enums.Tenants.APP_SERVICE_ITEM_BILLING_INTERVAL.YEAR:
          return this.$t('BilledYearly');

        default:
          return this.$t('BilledMonthly');
      }
    },
    gstAmount() {
      return this.subtotal * (this.gst / 100);
    },
    extraItemsTotal() {
      return this.extraItems.reduce((total, currentValue) => total + currentValue.price, 0);
    },
    productPriceWithDiscount() {
      if (!this.appliedPromotionCode || this.appliedPromoCodeIsInvalid) {
        return this.productPrice;
      }

      if (this.appliedPromotionCode.coupon.amount_off) {
        return this.productPrice - (this.appliedPromotionCode.coupon.amount_off / 100) < 0
          ? 0
          : this.productPrice - (this.appliedPromotionCode.coupon.amount_off / 100);
      }

      if (this.appliedPromotionCode.coupon.percent_off) {
        return this.productPrice
        - (this.productPrice * (this.appliedPromotionCode.coupon.percent_off / 100));
      }

      return this.productPrice;
    },
    subtotal() {
      return this.productPriceWithDiscount + this.extraItemsTotal;
    },
    total() {
      return this.subtotal + this.totalTaxAmount;
    },
    isValidPay() {
      return !this.payWithNewCard || this.isCreditCardValid;
    },
    defaultPaymentMethod() {
      return this.paymentMethods.find(
        (method) => method.defaultPaymentMethod,
      ) || null;
    },
    taxBreakdown() {
      return this.taxCalculation && this.taxCalculation.tax_breakdown
        ? this.taxCalculation.tax_breakdown
        : [];
    },
    taxBreakdownFormatted() {
      return this.taxBreakdown.map((tax) => {
        const type = tax.tax_rate_details.tax_type.toUpperCase();
        const percentage = tax.tax_rate_details.percentage_decimal;
        const amount = tax.amount / 100;

        return {
          label: `${type} (${percentage}%)`,
          amount,
        };
      });
    },
    totalTaxAmount() {
      if (!this.taxBreakdown.length) return 0;

      return this.taxBreakdown.reduce((sum, tax) => sum + (tax.amount / 100), 0);
    },
  },
  watch: {
    promoCode(val) {
      this.promoCode = val.toUpperCase();
    },
    payWithNewCard() {
      this.isCreditCardValid = false;
    },
    selectedInterval(val) {
      this.$emit('update:interval', val);
    },
    appliedPromotionCode() {
      if (this.appliedPromotionCode && !this.appliedPromoCodeIsInvalid) {
        this.$emit('promo-code', this.appliedPromotionCode.id);
      } else {
        this.$emit('promo-code', '');
      }
    },
    productPrice() {
      this.getTaxCalculation();
    },
  },
  created() {
    this.selectedPaymentMethodLocal = this.defaultPaymentMethod;
    this.inputSelectedPaymentMethod();

    this.getTaxCalculation();
  },
  methods: {
    async getTaxCalculation() {
      this.loading = true;
      const response = await tenantService.getCustomerTaxCalculation({
        productName: this.productName,
        price: this.productPriceWithDiscount,
      });

      this.taxCalculation = response.data;

      this.loading = false;
    },
    async paymentIntent() {
      let payload = null;

      if (this.payWithNewCard) {
        payload = await this.$refs.addCreditCard.addCard();
      }

      this.$emit('payment-intent', payload);
    },
    inputSelectedPaymentMethod() {
      this.$emit('update:selected-payment-method', this.selectedPaymentMethodLocal);
      this.$emit('input-payment-method');
    },
    async applyPromoCode() {
      this.loading = true;

      const response = await tenantService.getPromotionCode(this.promoCode);

      this.appliedPromotionCode = response.data;
      await this.getTaxCalculation();
      this.loading = false;
    },
    async removePromoCode() {
      this.promoCode = '';
      this.appliedPromotionCode = undefined;
      this.addPromotionCode = false;
      await this.getTaxCalculation();
    },
  },
};
</script>

<style lang="scss">
.app-checkout {
  .promo-code-container {
    position: relative;

    .apply-button {
      position: absolute;
      right: 10px;
      top: 50%;
      transform: translateY(-50%);
      z-index: 2;
      height: 32px;
      display: flex;
      align-items: center;
    }

    ::v-deep .vs-con-input {
      .vs-inputx {
        padding-right: 70px !important;
      }
    }
  }
}

</style>
